/**
 * SectionHeader - Standardized header for content sections
 *
 * Pattern: Title / Subtitle | Filters | Action
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { SECTION_TITLE } from '@/lib/typography';

export interface SectionHeaderProps {
  /** Section title */
  title?: ReactNode;
  /** Appends animated ellipsis to the title while true — the app's standard loading indicator */
  loading?: boolean;
  /** Optional action on the right (toggle, button, etc) */
  action?: ReactNode;
  /** If provided, title becomes a link */
  href?: string;
  /** Optional click handler on the title link (e.g. tab-aware navigation) */
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  /** Additional className for the container */
  className?: string;
  /** Additional className for the title (the bar's title takes the headline role) */
  titleClassName?: string;
}

export function SectionHeader({
  title,
  loading = false,
  action,
  href,
  onClick,
  className,
  titleClassName,
}: SectionHeaderProps) {
  const hasLeftContent = Boolean(title);

  const titleContent = hasLeftContent ? (
    <div className="flex items-center gap-2.5 min-w-0">
      {title && (
        <h2 className={cn('min-w-0 truncate text-foreground', SECTION_TITLE, titleClassName, loading && 'loading-dots')}>
          {title}
        </h2>
      )}
    </div>
  ) : null;

  return (
    <div className={cn('section-heading', className)}>
      {/* Left side: Icon + Title/Subtitle */}
      {titleContent && (
        <div className="section-heading-title">
          {href ? (
            <Link to={href} onClick={onClick} className=" transition-opacity">
              {titleContent}
            </Link>
          ) : (
            titleContent
          )}
        </div>
      )}

      {/* Right side: Action */}
      {action && (
        <div className="action-row section-heading-actions">
          {action}
        </div>
      )}
    </div>
  );
}

